import { useI18n } from 'next-localization';

import { useOasApp } from '@/providers';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

type FormattedTitleParams = {
  fallbackTitle?: Field<string>;
  returnType?: 'string' | 'field';
};

/**
 * Custom hook to generate a formatted site title for OAS pages.
 *
 * Combines the current page's title (from Sitecore context or fallback) with a localized suffix.
 * Returns either a formatted string or a Field<string> object, based on the `returnType` parameter.
 *
 * Currently this is derived from oas hostTitle + localized 'OAS.LandingPage.Title' dinctionary suffix,
 * e.g. "HCA Florida" + "Doctors"
 *
 * @param params - Optional parameters for formatting the title.
 * @param params.fallbackTitle - Fallback Field<string> to use if no route or page title is available.
 * @param params.returnType - Determines the return type: `'string'` (default) or `'field'`.
 * @returns The formatted site title as a string or Field<string>, depending on `returnType`.
 */
export const useFormatedSiteTitle = (params?: FormattedTitleParams): Field<string> | string => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const { pageTitle, isMho } = useOasApp();
  const routeExists = sitecoreContext?.route !== undefined;

  // Default values
  const { fallbackTitle = { value: '' }, returnType = 'string' } = params || {};

  const titleField = routeExists
    ? pageTitle || (sitecoreContext.route?.fields?.pageTitle as Field<string>)
    : fallbackTitle;

  const mhoTitle = t('OAS.LandingPage.Title');
  const siteTitle = titleField?.value ? `${titleField.value} ${t('OAS.LandingPage.Title')}` : '';

  const formattedTitle = isMho ? mhoTitle : siteTitle;

  if (returnType === 'field') {
    return {
      value: formattedTitle,
    } as Field<string>;
  }

  return formattedTitle;
};
